import React, { useCallback, useEffect, useRef, useState } from "react";

const ROUND_TIMES = [5, 9, 9, 10, 10, 9, 8];
const MAX_BOXES_PER_ROUND = [12, 16, 22, 25, 30, 38, 47];

export default function RoundSystem({
  started,
  onRoundEnd,
  onRoundStateChange,
  leftCount,
  rightCount,
  onResetCounts,
  onBoxesGenerated,
  onScoreUpdate,
}) {
  const roundMax = 7;
  const [round, setRound] = useState(1);
  const [timeLeft, setTimeLeft] = useState(ROUND_TIMES[0]);
  const [isRunning, setIsRunning] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [showAnswers, setShowAnswers] = useState(false);
  const [roundStart, setRoundStart] = useState(false);
  const [rightCheck, setRightCheck] = useState("");
  const [leftCheck, setLeftCheck] = useState("");
  const [leftBoxCount, setLeftBoxCount] = useState(0);
  const [rightBoxCount, setRightBoxCount] = useState(0);
  const [leftScore, setLeftScore] = useState(0);
  const [rightScore, setRightScore] = useState(0);
  const [boxesShown, setBoxesShown] = useState(false);

  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const leftCountRef = useRef(leftCount);
  const rightCountRef = useRef(rightCount);
  const leftScoreRef = useRef(0);
  const rightScoreRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  useEffect(() => {
    leftCountRef.current = leftCount;
    rightCountRef.current = rightCount;
  }, [leftCount, rightCount]);

  useEffect(() => {
    leftScoreRef.current = leftScore;
    rightScoreRef.current = rightScore;
  }, [leftScore, rightScore]);

  useEffect(() => {
    const isActive =
      roundStart && !showAnswers && countdown === null && boxesShown;
    onRoundStateChange?.(isActive);
  }, [roundStart, showAnswers, countdown, boxesShown, onRoundStateChange]);

  const beginRound = useCallback((roundNumber) => {
    clearTimer();
    clearCountdown();
    setRound(roundNumber);
    setTimeLeft(ROUND_TIMES[roundNumber - 1]);
    setShowAnswers(false);
    setBoxesShown(false);
    setRoundStart(false);
    setCountdown(3);
    onResetCounts?.();
    setRightCheck("");
    setLeftCheck("");

    let count = 3;
    countdownRef.current = setInterval(() => {
      count -= 1;

      if (count > 0) {
        setCountdown(count);
      } else if (count === 0) {
        setCountdown("GO!");
      } else {
        clearCountdown();
        setCountdown(null);
        setRoundStart(true);
        setIsRunning(true);
      }
    }, 1000);
  }, [clearCountdown, clearTimer, onResetCounts]);

  useEffect(() => {
    if (started && !isRunning && round === 1 && !roundStart && !boxesShown) {
      beginRound(1);
    }
  }, [started, isRunning, round, roundStart, boxesShown, beginRound]);

  useEffect(() => {
    if (roundStart && countdown === null && !boxesShown && !showAnswers) {
      const maxBoxesPerSide = MAX_BOXES_PER_ROUND[round - 1];
      const totalCells = 100;
      const nextLeftBoxCount =
        Math.floor(Math.random() * (maxBoxesPerSide * 0.4 + 1)) +
        Math.floor(maxBoxesPerSide * 0.6);
      const nextRightBoxCount =
        Math.floor(Math.random() * (maxBoxesPerSide * 0.4 + 1)) +
        Math.floor(maxBoxesPerSide * 0.6);
      const totalBoxes = nextLeftBoxCount + nextRightBoxCount;
      const positions = [];

      while (positions.length < totalBoxes) {
        const randomPosition = Math.floor(Math.random() * totalCells);
        if (!positions.includes(randomPosition)) {
          positions.push(randomPosition);
        }
      }

      const boxes = positions.map((position, index) => ({
        index: position,
        side: index < nextLeftBoxCount ? "left" : "right",
      }));

      setLeftBoxCount(nextLeftBoxCount);
      setRightBoxCount(nextRightBoxCount);
      onBoxesGenerated?.(boxes);
      setBoxesShown(true);
    }
  }, [roundStart, countdown, boxesShown, showAnswers, round, onBoxesGenerated]);

  useEffect(() => {
    if (!isRunning || showAnswers || countdown !== null || !boxesShown) {
      return undefined;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((previousTime) => {
        if (previousTime > 1) {
          return previousTime - 1;
        }

        clearTimer();
        setRoundStart(false);
        setIsRunning(false);

        setTimeout(() => {
          const currentLeftCount = leftCountRef.current;
          const currentRightCount = rightCountRef.current;
          const leftCorrect = currentLeftCount === leftBoxCount;
          const rightCorrect = currentRightCount === rightBoxCount;
          const newLeftScore = leftScoreRef.current + (leftCorrect ? 1 : 0);
          const newRightScore = rightScoreRef.current + (rightCorrect ? 1 : 0);

          setLeftCheck(leftCorrect ? "✅" : "❌");
          setRightCheck(rightCorrect ? "✅" : "❌");
          setLeftScore(newLeftScore);
          setRightScore(newRightScore);
          onScoreUpdate?.({ left: newLeftScore, right: newRightScore });
        }, 100);

        setShowAnswers(true);
        return 0;
      });
    }, 1000);

    return () => {
      clearTimer();
    };
  }, [
    boxesShown,
    clearTimer,
    countdown,
    isRunning,
    leftBoxCount,
    onScoreUpdate,
    rightBoxCount,
    showAnswers,
  ]);

  useEffect(() => {
    if (!showAnswers) return undefined;

    const timeout = setTimeout(() => {
      setShowAnswers(false);
      setBoxesShown(false);

      if (round < roundMax) {
        beginRound(round + 1);
      } else {
        setIsRunning(false);
        onRoundEnd?.();
      }
    }, 3500);

    return () => clearTimeout(timeout);
  }, [showAnswers, round, roundMax, onRoundEnd, beginRound]);

  useEffect(() => {
    return () => {
      clearTimer();
      clearCountdown();
    };
  }, [clearTimer, clearCountdown]);

  return (
    <div
      style={{
        width: "100%",
        marginBottom: "20px",
        padding: "18px 22px",
        background: "rgba(255, 255, 255, 0.08)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        backdropFilter: "blur(12px)",
        color: "white",
        borderRadius: "18px",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
        textAlign: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "18px",
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "clamp(1.4rem, 3vw, 2rem)" }}>
          ROUND {round} / {roundMax}
        </h2>
        <span style={{ color: "#f39c12", fontSize: "1.4rem" }}>•</span>
        <h3
          style={{
            margin: 0,
            fontSize: "clamp(1.2rem, 2.5vw, 1.7rem)",
            color: countdown !== null ? "#f1c40f" : "#ff8f70",
          }}
        >
          {countdown !== null ? countdown : `${timeLeft}s`}
        </h3>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "18px",
          flexWrap: "wrap",
          marginTop: "14px",
        }}
      >
        <div
          style={{
            minWidth: "160px",
            padding: "12px 16px",
            borderRadius: "14px",
            background: "rgba(52, 152, 219, 0.16)",
            border: "1px solid rgba(52, 152, 219, 0.4)",
          }}
        >
          <div style={{ color: "#9ed8ff", fontSize: "0.9rem" }}>Left Score</div>
          <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>{leftScore}</div>
        </div>
        <div
          style={{
            minWidth: "160px",
            padding: "12px 16px",
            borderRadius: "14px",
            background: "rgba(231, 76, 60, 0.16)",
            border: "1px solid rgba(231, 76, 60, 0.4)",
          }}
        >
          <div style={{ color: "#ffb0a9", fontSize: "0.9rem" }}>
            Right Score
          </div>
          <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>{rightScore}</div>
        </div>
      </div>

      {showAnswers ? (
        <div style={{ marginTop: "16px", color: "#e8f6ff" }}>
          <h3 style={{ margin: "0 0 10px 0", fontSize: "1.4rem" }}>
            Round Results
          </h3>
          <p style={{ margin: "6px 0" }}>
            Left: {leftCount} {leftCheck} | Correct: {leftBoxCount}
          </p>
          <p style={{ margin: "6px 0" }}>
            Right: {rightCount} {rightCheck} | Correct: {rightBoxCount}
          </p>
        </div>
      ) : countdown === null ? (
        <p style={{ margin: "16px 0 0 0", color: "#cfd9e6", fontSize: "1rem" }}>
          Current counts: Left {leftCount} | Right {rightCount}
        </p>
      ) : (
        <p style={{ margin: "16px 0 0 0", color: "#cfd9e6", fontSize: "1rem" }}>
          {round === 1
            ? "Get ready for the first round."
            : "Next round is about to start."}
        </p>
      )}
    </div>
  );
}
